import { useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import Button from "../../commons/Button/Button";
import { FormField } from "../../commons/FormField/FormField";
import "../../css/ScrollbarStyles.css";
import useSubUser from "../../state/hooks/users/useSubUser";
import TextInput from "../../commons/TextInput/TextInput";
import Typography from "../../commons/Typography/Typography";
import useMe from "../../state/hooks/auth/useMe";
import { DropdownWithSearch } from "../../commons/Dropdown";
import useLanguage from "../../state/hooks/languages/hook/useLanguage";
import { useMemo } from "react";
import useAppParams from "../../state/hooks/app/useAppParams";
import useUpdateSubUser from "../../state/hooks/users/useUpdateSubUser";
import timezones from "timezones-list";
import subUserSchema from "../../models/schemas/sub-user.schema";
import SubUserInitialValues from "../../models/initial-values/subUser-initial-values";
import { InputErrorMessage } from "../../commons/error";
import useDeleteSubUser from "../../state/hooks/users/useDeleteSubUser";
import getChangedValues from "../../utils/helpers/getChangedValues";
import FormatDate from "../../utils/helpers/formatDate";
import { User } from "../../models/Interface/User.interface";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../state/querys/querys.enum";
import { useState } from "react";
import { packageOptions } from "../../models/enum/package.enum";

type Option = {
  value: string;
  label: string;
};

type FieldMap = Record<string, string>;

const SubProfile = () => {
  const query = useLocation();
  const userId = query.pathname.replace("/sub-users/", "");
  const { data: meData, isLoading: mLoading } = useMe();
  const { data, isLoading: LLoading } = useLanguage();
  const { data: AppData } = useAppParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refetchSubUser = () => {
    queryClient.invalidateQueries({
      queryKey: [
        QueryKeys.SUB_USER,
        { userId: meData?.id === userId ? undefined : userId },
      ],
    });
  };

  const handleGoToSubscription = (e: React.MouseEvent<HTMLButtonElement>) => {
    const url = `/subscriptions?subUserId=${userId}`;
    if (e.metaKey || e.ctrlKey) {window.open(url, "_blank");}
    else {navigate(url);}
  };
  const handleGoToDeviceList = (e: React.MouseEvent<HTMLButtonElement>) => {
    const url = `/devicelist?subUserId=${userId}`;
   if (e.metaKey || e.ctrlKey) {window.open(url, "_blank");}
    else {navigate(url);}
  };

  const { mutateDeleteUser, isPending: UDeleteSubUser } = useDeleteSubUser({
    onSuccess: () => {
      alert("Deleted SubUser success");
      navigate("/sub-users");
      refetchSubUser();
    },
    onError: (error: unknown) => {
      alert(`Failed to delete SubUser: ${String(error)}`);
    },
  });

  const handleDeleteSubUser = () => {
    const question = confirm("Are you sure you want to delete this user?");
    if (question) {
      mutateDeleteUser({ subUserId: userId });
    }
  };

  const [formSubmitting, setFormSubmitting] = useState(false);

  const { mutate, isPending: UPending } = useUpdateSubUser({
    onSuccess: () => {
      refetchSubUser();
      setFormSubmitting(false);
    },
    onError: (error) => {
      console.error("Update failed:", error);
      alert(
        `Failed to update: ${
          error?.response?.data?.message || "Unknown error occurred"
        }`
      );
      setFormSubmitting(false);
    },
  });

  const sortedActiveLangs = useMemo(
    () => data?.languages?.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [data?.languages]
  );

  const ModifiedNativeLang: Option[] | undefined = useMemo(
    () =>
      sortedActiveLangs?.map(({ slug, name }) => ({
        value: slug,
        label: name,
      })),
    [sortedActiveLangs]
  );

  const ModifiedTargetLang: Option[] | undefined = useMemo(() => {
    return sortedActiveLangs
      ?.filter((lang) => lang.target)
      .map(({ slug, name }) => ({ value: slug, label: name }));
  }, [sortedActiveLangs]);

  const memoizeField = (fieldData?: FieldMap): Option[] => {
    if (!fieldData) return [];
    return Object.entries(fieldData).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  };

  const ModifiedSourceData = useMemo(
    () => memoizeField(AppData?.data?.source),
    [AppData?.data?.source]
  );
  const ModifiedTimeZoneData: Option[] = useMemo(() => {
    const uniqueTimezones = new Map<string, Option>();
    timezones.forEach((tz) => {
      const label = `UTC${tz.utc}`;
      if (!uniqueTimezones.has(label)) {
        uniqueTimezones.set(label, { value: label, label });
      }
    });
    return Array.from(uniqueTimezones.values());
  }, []);

  const ModifiedLevelsData = useMemo(
    () => memoizeField(AppData?.data?.levels),
    [AppData?.data?.levels]
  );
  const ModifiedGoalsData = useMemo(
    () => memoizeField(AppData?.data?.goals),
    [AppData?.data?.goals]
  );
  const ModifiedFrequenciesData = useMemo(
    () => memoizeField(AppData?.data?.frequencies),
    [AppData?.data?.frequencies]
  );

  // packageOptions imported from ../../models/enum/package.enum

  const {
    data: UData,
    isLoading,
    isPending,
    isError,
    error,
  } = useSubUser(meData?.id === userId ? undefined : userId);

  const handleGoToMainUser = (e: React.MouseEvent<HTMLButtonElement>) => {
    const url = `/main-users/${UData?.data?.userId}`;
    if (e.metaKey || e.ctrlKey) {window.open(url, "_blank");}
    else {navigate(url);}
    
  };

  if (isLoading || isPending || mLoading || LLoading)
    return <div>Loading...</div>;

  if (isError && error)
    return (
      <div className="flex flex-1 justify-center items-center h-full">
        <Typography>
          {JSON.stringify((error as any)?.response?.data.message)}
        </Typography>
      </div>
    );

  const initialValues = SubUserInitialValues({
    UData: UData || undefined,
    AppData: AppData || undefined,
  });
  const handleSubmit = async (
    values: Partial<User>,
    { setSubmitting, validateForm }: any
  ) => {
    try {
      setFormSubmitting(true);

      // Validate form first
      const errors = await validateForm(values);
      if (Object.keys(errors).length > 0) {
        console.error("Validation errors:", errors);
        alert("Please fix the validation errors before submitting.");
        setFormSubmitting(false);
        setSubmitting(false);
        return;
      }

      const changedValues = getChangedValues(initialValues, values);
      if (Object.keys(changedValues).length > 0) {
        // Start the update process
        mutate({ userId, data: changedValues });
      } else {
        alert("No changes to update");
        setFormSubmitting(false);
      }
    } catch (error) {
      console.error("Error during submission:", error);
      alert(`An error occurred during submission: ${String(error)}`);
      setFormSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <div className="flex overflow-x-auto px-30 pt-80 pb-20 flex-col bg-light-grey-1 h-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-dark-grey font-bold text-2xl">Profile</h2>
        <div className="flex gap-2">
          <Button
            type="secondary"
            buttonType="button"
            id="Profile.GoToDeviceListInfoButton"
            onClick={handleGoToDeviceList}
          >
            Go to device list
          </Button>
          <Button
            type="secondary"
            buttonType="button"
            id="Profile.GoToSubscriptionButton"
            onClick={handleGoToSubscription}
          >
            Go to Subscription
          </Button>
          <Button
            type="secondary"
            buttonType="button"
            id="Profile."
            onClick={handleGoToMainUser}
          >
            Go to main user
          </Button>
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={subUserSchema}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {(formik) => {
          return (
            <form onSubmit={formik.handleSubmit}>
              <div className="mt-50 mb-30">
                <FormField label="First name">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue("firstName", e?.target.value)
                    }
                    id="Profile.FirstNameField"
                    value={formik.values.firstName}
                    name="firstName"
                  />
                  {formik.errors.firstName && (
                    <InputErrorMessage props={formik} type="firstName" />
                  )}
                </FormField>
                <FormField label="Last name">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue("lastName", e?.target.value)
                    }
                    id="Profile.LastNameField"
                    value={formik.values.lastName}
                    name="lastName"
                  />
                  {formik.errors.lastName && (
                    <InputErrorMessage props={formik} type="lastName" />
                  )}
                </FormField>
                <FormField label="Creation Date">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue("createdAt", e?.target.value)
                    }
                    id="Profile.CreationDate"
                    value={FormatDate(formik.values.createdAt) || ""}
                    name="createdAt"
                    disabled
                  />
                </FormField>
                <FormField label="Email (required)">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={formik.handleChange}
                    id="Profile.EmailField"
                    value={formik.values.email}
                    name="email"
                  />
                  {formik.errors.email && (
                    <InputErrorMessage props={formik} type="email" />
                  )}
                </FormField>
                <FormField label="Package">
                  <DropdownWithSearch
                    options={packageOptions ?? []}
                    className="w-360"
                    onChange={(e) => formik.setFieldValue("package", e?.value)}
                    id="Subscription.PackageSelect"
                    value={formik.values.package}
                  />
                  {formik.errors.package && (
                    <InputErrorMessage props={formik} type="package" />
                  )}
                </FormField>
                <FormField label="Source">
                  <DropdownWithSearch
                    options={ModifiedSourceData ?? []}
                    onChange={(e) => formik.setFieldValue("source", e?.value)}
                    className="w-360"
                    id="Profile.SourceSelect"
                    value={formik.values.source}
                  />
                  {formik.errors.source && (
                    <InputErrorMessage props={formik} type="source" />
                  )}
                </FormField>
                <FormField label="Target language">
                  <DropdownWithSearch
                    options={ModifiedTargetLang ?? []}
                    className="w-360"
                    onChange={(e) =>
                      formik.setFieldValue("targetLanguage", e?.value)
                    }
                    id="Profile.TargetLanguageSelect"
                    value={formik.values.targetLanguage}
                  />
                  {formik.errors.targetLanguage && (
                    <InputErrorMessage props={formik} type="targetLanguage" />
                  )}
                </FormField>
                <FormField label="Current level">
                  <DropdownWithSearch
                    options={ModifiedLevelsData ?? []}
                    className="w-360"
                    onChange={(e) =>
                      formik.setFieldValue("currentLevel", e?.value)
                    }
                    id="Profile.CurrentLevelSelect"
                    value={formik.values.currentLevel}
                  />
                  {formik.errors.currentLevel && (
                    <InputErrorMessage props={formik} type="currentLevel" />
                  )}
                </FormField>
                <FormField label="Primary goal">
                  <DropdownWithSearch
                    options={ModifiedGoalsData ?? []}
                    className="w-360"
                    onChange={(e) =>
                      formik.setFieldValue("primaryGoal", e?.value)
                    }
                    id="Profile.PrimaryGoalSelect"
                    value={formik.values.primaryGoal}
                  />
                  {formik.errors.primaryGoal && (
                    <InputErrorMessage props={formik} type="primaryGoal" />
                  )}
                </FormField>
                <FormField label="Native language">
                  <DropdownWithSearch
                    options={ModifiedNativeLang ?? []}
                    className="w-360"
                    onChange={(e) =>
                      formik.setFieldValue("nativeLanguage", e?.value)
                    }
                    id="Profile.NativeLanguageSelect"
                    value={formik.values.nativeLanguage}
                  />
                  {formik.errors.nativeLanguage && (
                    <InputErrorMessage props={formik} type="nativeLanguage" />
                  )}
                </FormField>
                <FormField label="Practice frequency">
                  <DropdownWithSearch
                    options={ModifiedFrequenciesData ?? []}
                    className="w-360"
                    onChange={(e) =>
                      formik.setFieldValue("practiceFrequency", e?.value)
                    }
                    id="Profile.PracticeFrequencySelect"
                    value={formik.values.practiceFrequency}
                  />
                  {formik.errors.practiceFrequency && (
                    <InputErrorMessage
                      props={formik}
                      type="practiceFrequency"
                    />
                  )}
                </FormField>
                <FormField label="Interface language">
                  <DropdownWithSearch
                    options={ModifiedNativeLang ?? []}
                    className="w-360"
                    onChange={(e) =>
                      formik.setFieldValue("interfaceLanguage", e?.value)
                    }
                    id="Profile.InterfaceLanguageSelect"
                    value={formik.values.interfaceLanguage}
                  />
                  {formik.errors.interfaceLanguage && (
                    <InputErrorMessage
                      props={formik}
                      type="interfaceLanguage"
                    />
                  )}
                </FormField>
                <FormField label="Time Zone">
                  <DropdownWithSearch
                    options={ModifiedTimeZoneData ?? []}
                    className="w-360"
                    onChange={(e) => formik.setFieldValue("timeZone", e?.value)}
                    id="Profile.TimeZoneSelect"
                    value={formik.values.timeZone}
                  />
                  {formik.errors.timeZone && (
                    <InputErrorMessage props={formik} type="timeZone" />
                  )}
                </FormField>
                <div className="grid grid-cols-2 gap-x-2 gap-y-4">
                  <FormField label="Mute">
                    <input
                      type="checkbox"
                      checked={formik.values.mute}
                      onChange={formik.handleChange}
                      name="mute"
                      id="Profile.MuteCheckbox"
                    />
                    {formik.errors.mute && (
                      <InputErrorMessage props={formik} type="mute" />
                    )}
                  </FormField>
                  <FormField label="Sentence mode mute">
                    <input
                      type="checkbox"
                      checked={formik.values.sentenceMute}
                      onChange={formik.handleChange}
                      name="sentenceMute"
                      id="Profile.SentenceModeMuteCheckbox"
                    />
                    {formik.errors.sentenceMute && (
                      <InputErrorMessage props={formik} type="sentenceMute" />
                    )}
                  </FormField>
                  <FormField label="Had Premium">
                    <input
                      type="checkbox"
                      checked={formik.values.hadPremium}
                      onChange={formik.handleChange}
                      name="hadPremium"
                      id="Profile.HadPremiumCheckbox"
                    />
                    {formik.errors.hadPremium && (
                      <InputErrorMessage props={formik} type="hadPremium" />
                    )}
                  </FormField>
                  <FormField label="Premium">
                    <input
                      type="checkbox"
                      checked={formik.values.premium}
                      onChange={formik.handleChange}
                      name="premium"
                      id="Profile.PremiumCheckbox"
                    />
                    {formik.errors.premium && (
                      <InputErrorMessage props={formik} type="premium" />
                    )}
                  </FormField>
                  <FormField label="Is Suggest">
                    <input
                      type="checkbox"
                      checked={formik.values.isSuggest}
                      onChange={formik.handleChange}
                      name="isSuggest"
                      id="Profile.IsSuggestCheckbox"
                    />
                    {formik.errors.isSuggest && (
                      <InputErrorMessage props={formik} type="isSuggest" />
                    )}
                  </FormField>
                  <FormField label="Is Hands Free">
                    <input
                      type="checkbox"
                      checked={formik.values.isHandsFree}
                      onChange={formik.handleChange}
                      name="isHandsFree"
                      id="Profile.IsHandsFreeCheckbox"
                    />
                    {formik.errors.isHandsFree && (
                      <InputErrorMessage props={formik} type="isHandsFree" />
                    )}
                  </FormField>
                  <FormField label="Is Hands Free Call Mode">
                    <input
                      type="checkbox"
                      checked={formik.values.isHandsFreeCallMode}
                      onChange={formik.handleChange}
                      name="isHandsFreeCallMode"
                      id="Profile.IsHandsFreeCallModeCheckbox"
                    />
                    {formik.errors.isHandsFreeCallMode && (
                      <InputErrorMessage
                        props={formik}
                        type="isHandsFreeCallMode"
                      />
                    )}
                  </FormField>
                  <FormField label="Is Hands Free Sentence Mode">
                    <input
                      type="checkbox"
                      checked={formik.values.isHandsFreeSentenceMode}
                      onChange={formik.handleChange}
                      name="isHandsFreeSentenceMode"
                      id="Profile.IsHandsFreeSentenceModeCheckbox"
                    />
                    {formik.errors.isHandsFreeSentenceMode && (
                      <InputErrorMessage
                        props={formik}
                        type="isHandsFreeSentenceMode"
                      />
                    )}
                  </FormField>
                  <FormField label="Test Mode For User">
                    <input
                      type="checkbox"
                      checked={formik.values.isTestUser}
                      onChange={(e) =>
                        formik.setFieldValue("isTestUser", e.target.checked)
                      }
                      name="isTestUser"
                      id="Profile.isTestUser"
                    />
                    {formik.errors.isTestUser && (
                      <InputErrorMessage props={formik} type="isTestUser" />
                    )}
                  </FormField>
                  <FormField label="Is Dark Mode">
                    <input
                      type="checkbox"
                      checked={formik.values.isDarkMode}
                      onChange={formik.handleChange}
                      name="isDarkMode"
                      id="Profile.IsDarkModeCheckbox"
                    />
                    {formik.errors.isDarkMode && (
                      <InputErrorMessage props={formik} type="isDarkMode" />
                    )}
                  </FormField>
                  <FormField label="UI Sound">
                    <input
                      type="checkbox"
                      checked={formik.values.uiSound}
                      onChange={formik.handleChange}
                      name="uiSound"
                      id="Profile.UiSoundCheckbox"
                    />
                    {formik.errors.uiSound && (
                      <InputErrorMessage props={formik} type="uiSound" />
                    )}
                  </FormField>
                  <FormField label="Seen Tutorial">
                    <input
                      type="checkbox"
                      checked={formik.values.seenTutorial}
                      onChange={formik.handleChange}
                      name="seenTutorial"
                      id="Profile.SeenTutorialCheckbox"
                    />
                    {formik.errors.seenTutorial && (
                      <InputErrorMessage props={formik} type="seenTutorial" />
                    )}
                  </FormField>
                  <FormField label="Eligible For Refund">
                    <input
                      type="checkbox"
                      checked={formik.values.eligibleForRefund}
                      onChange={formik.handleChange}
                      name="eligibleForRefund"
                      id="Profile.EligibleForRefundCheckbox"
                    />
                    {formik.errors.eligibleForRefund && (
                      <InputErrorMessage
                        props={formik}
                        type="eligibleForRefund"
                      />
                    )}
                  </FormField>
                  <FormField label="Newsletter">
                    <input
                      type="checkbox"
                      checked={formik.values.newsletter}
                      onChange={formik.handleChange}
                      name="newsletter"
                      id="Profile.NewsletterCheckbox"
                    />
                    {formik.errors.newsletter && (
                      <InputErrorMessage props={formik} type="newsletter" />
                    )}
                  </FormField>
                </div>
                <FormField label="Chat Name">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue("chatName", e?.target?.value)
                    }
                    id="Profile.ChatNameField"
                    value={formik.values.chatName}
                    name="chatName"
                  />
                  {formik.errors.chatName && (
                    <InputErrorMessage props={formik} type="chatName" />
                  )}
                </FormField>
                <FormField label="Speech Speed">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="number"
                    min="0.5"
                    max="1.5"
                    step="0.25"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "speechSpeed",
                        parseFloat(e?.target?.value)
                      )
                    }
                    id="Profile.SpeechSpeedField"
                    value={
                      typeof formik.values.speechSpeed === "number"
                        ? Number(formik.values.speechSpeed.toFixed(2))
                        : formik.values.speechSpeed || ""
                    }
                    name="speechSpeed"
                  />
                  {formik.errors.speechSpeed && (
                    <InputErrorMessage props={formik} type="speechSpeed" />
                  )}
                </FormField>
                <FormField label="AB Testing Version">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersion",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionField"
                    value={formik.values?.abTesting?.abTestingVersion}
                    name="abTesting.abTestingVersion"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 2">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot2",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot2Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot2}
                    name="abTesting.abTestingVersionSlot2"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 3">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot3",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot3Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot3}
                    name="abTesting.abTestingVersionSlot3"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 4">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot4",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot4Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot4}
                    name="abTesting.abTestingVersionSlot4"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 5">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot5",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot5Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot5}
                    name="abTesting.abTestingVersionSlot5"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 6">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot6",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot6Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot6}
                    name="abTesting.abTestingVersionSlot6"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>
                <FormField label="AB Testing Version Slot 7">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot7",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot7Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot7}
                    name="abTesting.abTestingVersionSlot7"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>
                <FormField label="AB Testing Version Slot 8">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot8",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot8Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot8}
                    name="abTesting.abTestingVersionSlot8"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>
                <FormField label="AB Testing Version Slot 9">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot9",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot9Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot9}
                    name="abTesting.abTestingVersionSlot9"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>
                <FormField label="AB Testing Version Slot 10">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot10",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot10Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot10}
                    name="abTesting.abTestingVersionSlot10"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 11">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot11",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot11Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot11}
                    name="abTesting.abTestingVersionSlot11"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 12">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot12",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot12Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot12}
                    name="abTesting.abTestingVersionSlot12"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 13">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot13",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot13Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot13}
                    name="abTesting.abTestingVersionSlot13"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 14">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot14",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot14Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot14}
                    name="abTesting.abTestingVersionSlot14"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 15">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot15",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot15Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot15}
                    name="abTesting.abTestingVersionSlot15"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 16">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot16",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot16Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot16}
                    name="abTesting.abTestingVersionSlot16"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 17">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot17",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot17Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot17}
                    name="abTesting.abTestingVersionSlot17"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 18">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot18",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot18Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot18}
                    name="abTesting.abTestingVersionSlot18"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 19">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot19",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot19Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot19}
                    name="abTesting.abTestingVersionSlot19"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>

                <FormField label="AB Testing Version Slot 20">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="text"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "abTesting.abTestingVersionSlot20",
                        e?.target?.value
                      )
                    }
                    id="Profile.ABTestingVersionSlot20Field"
                    value={formik.values?.abTesting?.abTestingVersionSlot20}
                    name="abTesting.abTestingVersionSlot20"
                  />
                  {formik.errors?.abTesting && (
                    <InputErrorMessage props={formik} type="abTesting" />
                  )}
                </FormField>
                <FormField label="Hands Free Interval">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="number"
                    min="0"
                    max="4"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "handsFreeInterval",
                        +e?.target?.value
                      )
                    }
                    id="Profile.HandsFreeIntervalField"
                    value={formik.values.handsFreeInterval}
                    name="handsFreeInterval"
                  />
                  {formik.errors.handsFreeInterval && (
                    <InputErrorMessage
                      props={formik}
                      type="handsFreeInterval"
                    />
                  )}
                </FormField>
                <FormField label="Hands Free Interval Call Mode">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="number"
                    min="0"
                    max="4"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "handsFreeIntervalCallMode",
                        +e?.target?.value
                      )
                    }
                    id="Profile.HandsFreeIntervalField"
                    value={formik.values.handsFreeIntervalCallMode}
                    name="handsFreeIntervalCallMode"
                  />
                  {formik.errors.handsFreeIntervalCallMode && (
                    <InputErrorMessage
                      props={formik}
                      type="handsFreeIntervalCallMode"
                    />
                  )}
                </FormField>
                <FormField label="Hands Free Interval Sentence Mode">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="number"
                    min="0"
                    max="4"
                    onChange={(e) =>
                      formik.setFieldValue(
                        "handsFreeIntervalSentenceMode",
                        +e?.target?.value
                      )
                    }
                    id="Profile.HandsFreeIntervalField"
                    value={formik.values.handsFreeIntervalSentenceMode}
                    name="handsFreeIntervalSentenceMode"
                  />
                  {formik.errors.handsFreeIntervalSentenceMode && (
                    <InputErrorMessage
                      props={formik}
                      type="handsFreeIntervalSentenceMode"
                    />
                  )}
                </FormField>
                <FormField label="Font Size">
                  <TextInput
                    customStyles="flex-col w-360"
                    placeholder=""
                    customInputStyles="h-12 w-full"
                    type="number"
                    step="0.1"
                    min="0"
                    onChange={(e) =>
                      formik.setFieldValue("fontSize", +e?.target?.value)
                    }
                    id="Profile.FontSizeField"
                    value={formik.values.fontSize}
                    name="fontSize"
                  />
                  {formik.errors.fontSize && (
                    <InputErrorMessage props={formik} type="fontSize" />
                  )}
                </FormField>
                <FormField label="Delete user">
                  <Button
                    type="secondary"
                    id="Profile.DeleteUserButton"
                    isLoading={UDeleteSubUser}
                    onClick={handleDeleteSubUser}
                    disabled={formik.values.email
                      ?.toLowerCase()
                      .includes("anonymous")}
                  >
                    Delete
                  </Button>
                </FormField>
              </div>
              <div>
                <Button
                  type="main"
                  id="Profile.UpdateButton"
                  isLoading={UPending || formSubmitting}
                  buttonType="submit"
                  disabled={UPending || formSubmitting}
                >
                  {UPending || formSubmitting ? "Updating..." : "Update"}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default SubProfile;
